import Image from 'app/Components/Shared/Image/image';
import React from 'react';
import Config from '../../../Config';
import { isMobile } from 'react-device-detect';
import './ProductHeroMarketPlace.scss';

const ProductHeroMarketPlace = ({ iContent, context }) => {
    const layout = iContent.layout?.value || 'Hero'; // Default to Hero

    // Helper function to get media URL
    const getMediaUrl = (contentReference) => {
        if (!contentReference?.value) return null;
        const url = contentReference.value?.url || contentReference.expandedValue?.url;
        return url ? Config.epiBaseUrl + url : null;
    };

    // Helper function to detect media type
    const getMediaType = (contentReference) => {
        if (!contentReference?.value) return null;
        const contentType = contentReference.expandedValue?.contentType?.[0] || '';
        const url = contentReference.value?.url || contentReference.expandedValue?.url || '';

        if (contentType.toLowerCase().includes('video') || url.toLowerCase().endsWith('.mp4')) {
            return 'video';
        }
        if (url.toLowerCase().endsWith('.gif')) {
            return 'gif';
        }
        return 'image';
    };

    // Get background media
    const backgroundMedia = isMobile ? iContent.mobileBackground : iContent.desktopBackground;
    const backgroundUrl = getMediaUrl(backgroundMedia);
    const backgroundType = getMediaType(backgroundMedia);

    // Get text content
    const mainText = iContent.mainText?.value;
    const mainTextColor = iContent.mainTextColor?.value || '#000000';
    const secondaryText = iContent.secondaryText?.value;
    const secondaryTextColor = iContent.secondaryTextColor?.value || '#000000';

    // Get button properties
    const buttonText = iContent.buttonText?.value;
    const buttonLink = iContent.buttonLink?.value;
    const buttonStyle = iContent.buttonStyle?.value || 'default';

    // Handle button click
    const handleButtonClick = () => {
        if (buttonLink) {
            window.location.href = buttonLink;
        }
    };

    const renderHeroLayout = () => {
        return (
            <div className="product-hero-marketplace__hero">
                {/* Background */}
                <div className="hero-background">
                    {backgroundUrl && backgroundType === 'video' && (
                        <video
                            className="hero-background__media"
                            autoPlay
                            loop
                            muted
                            playsInline
                        >
                            <source src={backgroundUrl} type="video/mp4" />
                        </video>
                    )}
                    {backgroundUrl && (backgroundType === 'image' || backgroundType === 'gif') && (
                        <Image className="hero-background__media"
                            src={backgroundUrl}
                            alt="" />
                    )}
                </div>

                {/* Content */}
                <div className="hero-content">
                    <div className="hero-content__wrapper">
                        {/* Text Section */}
                        <div className="hero-text">
                            {mainText && (
                                <div
                                    className="hero-text__main"
                                    style={{ color: mainTextColor }}
                                    dangerouslySetInnerHTML={{ __html: mainText }}
                                />
                            )}
                            {secondaryText && (
                                <div
                                    className="hero-text__secondary"
                                    style={{ '--text-color': secondaryTextColor }}
                                    dangerouslySetInnerHTML={{ __html: secondaryText }}
                                />
                            )}
                        </div>

                        {/* Button */}
                        {buttonText && buttonLink && (
                            <button
                                className={`hero-button hero-button--${buttonStyle}`}
                                onClick={handleButtonClick}
                            >
                                <span>{buttonText}</span>
                            </button>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    const renderMarketplaceLayout = () => {
        // Get marketplace buttons
        const marketplaceButtons = iContent.marketplaceButtons?.expandedValue || [];
        const marketplaceBackgroundColor = iContent.marketplaceBackgroundColor?.value || '#FFFFFF';

        // Count valid buttons (those with descriptionText)
        const validButtonsCount = marketplaceButtons.filter(btn => btn?.descriptionText?.value).length;
        const hasFiveOrMoreCards = validButtonsCount >= 5;
        const renderedCardsCount = Math.min(validButtonsCount, 5);
        const cardRows = renderedCardsCount > 0 ? Math.ceil(renderedCardsCount / 2) : 0;
        const mobileCardsHeight = cardRows > 0 ? cardRows * 50 + (cardRows - 1) * 8 : 0;
        const mobileBannerHeight = hasFiveOrMoreCards ? 600 : 550;
        // Pull cards onto the banner image (was 60px — too little; left a white band under the photo)
        const mobileServicesOverlap = hasFiveOrMoreCards ? 118 : 108;
        // Bottom gap under the card grid (5 cards = 3 rows): use explicit SCSS on .mktplace-services--five-plus; keep formula in sync
        const mobileServicesPaddingBottom = hasFiveOrMoreCards ? 72 : 23;
        const mobileMinHeight =
            mobileBannerHeight - mobileServicesOverlap + mobileCardsHeight + mobileServicesPaddingBottom;

        const marketplaceStyle = {
            '--mktplace-banner-height-mobile': `${mobileBannerHeight}px`,
            '--mktplace-services-overlap-mobile': `${mobileServicesOverlap}px`,
            '--mktplace-cards-height-mobile': `${mobileCardsHeight}px`,
            '--mktplace-min-height-mobile': `${mobileMinHeight}px`,
        };

        return (
            <div
                className="product-hero-marketplace__marketplace"
                style={marketplaceStyle}
            >
                {/* Top Banner Section */}
                <div className="mktplace-banner">
                    {/* Background */}
                    <div className="mktplace-banner__bg">
                        {backgroundUrl && backgroundType === 'video' && (
                            <video
                                className="mktplace-banner__media"
                                autoPlay
                                loop
                                muted
                                playsInline
                            >
                                <source src={backgroundUrl} type="video/mp4" />
                            </video>
                        )}
                        {backgroundUrl && (backgroundType === 'image' || backgroundType === 'gif') && (
                            <Image className="mktplace-banner__media"
                                src={backgroundUrl}
                                alt="" />
                        )}
                    </div>

                    {/* Content */}
                    <div className="mktplace-banner__content">
                        <div className="mktplace-text">
                            {mainText && (
                                <div
                                    className="mktplace-text__main"
                                    style={{ color: mainTextColor }}
                                    dangerouslySetInnerHTML={{ __html: mainText }}
                                />
                            )}
                            {secondaryText && (
                                <div
                                    className="mktplace-text__secondary"
                                    style={{ '--text-color': secondaryTextColor }}
                                    dangerouslySetInnerHTML={{ __html: secondaryText }}
                                />
                            )}
                        </div>
                    </div>
                </div>

                {/* Services/Cards Section */}
                <div
                    className={`mktplace-services${hasFiveOrMoreCards ? ' mktplace-services--five-plus' : ''}`}
                    style={{ backgroundColor: marketplaceBackgroundColor }}
                >
                    <div className="mktplace-cards-wrapper">
                        {marketplaceButtons.slice(0, 5).map((button, index) => {
                            // Only render if descriptionText exists
                            if (!button.descriptionText?.value) return null;

                            // Get icon URL from ContentReference
                            const iconUrl = button.icon?.expandedValue?.url
                                ? Config.epiBaseUrl + button.icon.expandedValue.url
                                : null;

                            // Get button link URL from ContentReference
                            const buttonUrl = button.buttonLink?.expandedValue?.url
                                ? button.buttonLink.expandedValue.url
                                : button.buttonLink?.value?.url
                                    ? button.buttonLink.value.url
                                    : button.buttonLink?.value || null;

                            const descriptionText = button.descriptionText?.value;

                            return (
                                <div
                                    key={index}
                                    className="mktplace-card"
                                    onClick={() => buttonUrl && (window.location.href = buttonUrl)}
                                >
                                    <div className="mktplace-card__inner">
                                        {iconUrl && (
                                            <div className="mktplace-card__icon">
                                                <Image src={iconUrl} alt="" />
                                            </div>
                                        )}
                                        <p className="mktplace-card__label">
                                            {descriptionText}
                                        </p>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        );
    };

    const marketplaceButtons = iContent.marketplaceButtons?.expandedValue || [];
    const marketplaceValidCount = marketplaceButtons.filter(btn => btn?.descriptionText?.value).length;
    const hasFiveOrMoreMarketplaceCards = marketplaceValidCount >= 5;

    return (
        <div
            className={[
                'product-hero-marketplace',
                `product-hero-marketplace--${layout.toLowerCase()}`,
                layout === 'Marketplace' && hasFiveOrMoreMarketplaceCards
                    ? 'product-hero-marketplace--five-plus'
                    : '',
            ].filter(Boolean).join(' ')}
        >
            {layout === 'Hero' && renderHeroLayout()}
            {layout === 'Marketplace' && renderMarketplaceLayout()}
        </div>
    );
};

export default ProductHeroMarketPlace;

